// @ts-check
/** @typedef {import('../types').Device} Device */
import React, { useRef, useState, useEffect } from 'react';
import { client } from '../utils/apiClient';
import { ViewErrorBoundary } from './ErrorFallback';
import { useAuthContext } from '../contexts/AuthContext';
import { useConnectionContext } from '../contexts/ConnectionContext';
import { useDeviceContext } from '../contexts/DeviceContext';
import { useUpgradeContext } from '../contexts/UpgradeContext';
import { useLogChange } from '../hooks/useLogChange';
import { useDeviceWriter } from '../hooks/useDeviceWriter';
import { devLog } from '../utils/devLog';
import { FIRMWARE_URL_KEYS, validateFirmwareUrl, normaliseFirmwareUrl } from '../utils/firmwareUrlStore';
import { createDevice, deleteDevice } from '../graphql/mutations';
import { REGS } from '../registers';

import { Card, Bold, Flex, Metric, Text as TextTremor, Title } from './TremorCompat';
import { Button, Icon } from './TremorCompat';
import ResizeObserver from 'resize-observer-polyfill';

import {
  MdOutlineArrowBack as IconBack,
  MdHotTub as IconSpa,
  MdRoofing as IconSolar,
  MdOutlineEdit as IconEdit,
  MdOutlineInsertChart as IconTrends,
  MdOutlineErrorOutline as IconError,
  // MdOutlineSpaceDashboard as IconDashboard,
  MdOutlineHome as IconHome,
  // MdRadioButtonUnchecked as IconCheck,
  MdQrCode2 as IconQRCode,
  MdOutlineSensors as IconConnected,
  MdOutlineSensorsOff as IconDisconnected,
  MdOutlineSync as IconSync,
  MdOutlineSettings as IconSettings,
  MdOutlineInfo as IconInfo,
  MdOutlineAddCircleOutline as IconAdd,
  MdOutlineRemoveCircleOutline as IconMinus,
} from 'react-icons/md';

import {
  // GiBubbles as IconBubbles,
  // GiChemicalDrop as IconChemical,

  // GiWinterHat as IconWinter,
  // GiValve as IconValve,
  GiThermometerCold as IconFrozen,
  GiDrippingTube as IconAcidTube,
  GiHeatHaze as IconHeat,
  // GiBoilingBubbles as IconBubbles2,
} from 'react-icons/gi';

import {
  TbEngine as IconPump,
  // TbPlug as IconSocket,
} from 'react-icons/tb';

import { handleEditDeviceUpdate } from './HomeView';

import { TrendsView } from './TrendsView';
import { SPAView } from './SPAView';
import { Notes } from './NoteComponents/Notes';
import { AppliancesView } from './AppliancesView';
import { SystemInfo } from './SystemInfo';
import { SystemSettingsView } from './SystemSettingsView';
import { ApplianceSetupView } from './AppliancesSetup';
import { FilterView } from './FilterView';
import { HeatingView } from './HeatingView';
import { AddInstaller, EditDevice, ShowDeviceQR, is_a_light_controller, is_a_SWC } from './Common';
import { DisplayItem, getControllerIconText_FROM_STRING, GetDeviceBadgeFromStatus } from './DisplayItems';
import { FirmwareUpgradeCard } from './FirmwareUpgradeCard';
import { MiddleEllipsis } from './MiddleEllipsis';

import { COLOURS } from '../styles';
import {
  locationsOptions,
  RECORD_TYPES,
  tabItems,
  APP_CONFIG,
  mType_label_t,
  upgradeResultCodes_t,
} from '../constants';

import {
  Affix,
  IconButton,
  InputPicker,
  Message,
  Navbar,
  Nav,
  Panel,
  RadioGroup,
  Radio,
  SelectPicker,
  // The module-level singleton, not useToaster(). This app mounts no
  // <CustomProvider>, so useToaster() has no custom toaster registry to read;
  // rsuite 5.65.1 happens to fall back to this same singleton, but 5.83.4
  // dropped that fallback and push() becomes a silent no-op — every error
  // toast in this file would vanish on a routine caret upgrade of "^5.65.1".
  // The singleton mounts its own container on document.body, so it works
  // standalone, and it is a stable module import rather than a hook result.
  toaster,
} from 'rsuite';

import { RxMixerVertical as IconControl } from 'react-icons/rx';

import { HistoryView } from './HistoryView';
import { SolarView } from './SolarView';
import { ControlView } from './ControlView';
import { ChemicalView } from './ChemicalView';
import { PumpView } from './PumpView';
import ScaleText from 'react-scale-text';
import {
  getHours,
  getMinutes,
  // format as DTformat
} from 'date-fns';
import { fixedHeaderCardStyle, unfixedHeaderCardStyle } from '../styles';
export const DeviceView = (props) => {
  const { loginId } = useAuthContext();
  const { connectionState, controllerResponded, registerData, syncing, setSyncing, doReadAll } = useConnectionContext();
  const { setNotesData, selectedDevice, updateDeviceName, updateUserDeviceName } = useDeviceContext();
  const { writeRegister, writePasswordRegister, writeValueStringRegister, writeRegisters } = useDeviceWriter();

  // Everything the firmware cards need, from useUpgradeFlow via context
  // rather than 49 props on this component. Aliased to the names used below;
  // the left-hand side is the hook's own key.
  const {
    upgradeFile,
    setUpgradeFile: onSetUpgradeFile,
    upgradeACFile,
    setUpgradeACFile: onSetUpgradeACFile,
    upgradeSHFile,
    setUpgradeSHFile: onSetUpgradeSHFile,
    upgradeLCFile,
    setUpgradeLCFile: onSetUpgradeLCFile,
    upgradeControlBoardFile,
    setUpgradeControlBoardFile: onSetUpgradeControlBoardFile,
    upgradePowerFile,
    setUpgradePowerFile: onSetUpgradePowerFile,
    upgradeIOFile,
    setUpgradeIOFile: onSetUpgradeIOFile,
    updateInProgress,
    setUpdateInProgress,
    updateInProgressAC,
    setACUpdateInProgress: onSetACUpdateInProgress,
    updateInProgressSH1,
    setSH1UpdateInProgress: onSetSH1UpdateInProgress,
    updateInProgressSH2,
    setSH2UpdateInProgress: onSetSH2UpdateInProgress,
    updateInProgressPower,
    setPowerUpdateInProgress: onSetPowerUpdateInProgress,
    updateInProgressIO,
    setIOUpdateInProgress: onSetIOUpdateInProgress,
    powerBoardSlot,
    setPowerBoardSlot,
    ioBoardSlot,
    setIOBoardSlot,
    powerUpgradeSlot,
    setPowerUpgradeSlot,
    ioUpgradeSlot,
    setIOUpgradeSlot,
    PSWUpgradeURLS,
    PSWUpgradeURLS_beta,
    AquaLightUpgradeURLS,
    AquaLightUpgradeURLS_beta,
    AquaChemUpgradeURLS,
    ControlBoardUpgradeURLS,
    PowerBoardUpgradeURLS,
    IOBoardUpgradeURLS,
    addPSWUrl: onaddPSWUrl,
    addACUrl: onaddACUrl,
    addAqualightUrl: onaddAqualightUrl,
    addControlBoardUrl: onaddControlBoardUrl,
    addPowerUrl: onaddPowerUrl,
    addIOUrl: onaddIOUrl,
    removeFirmwareUrl: onRemoveFirmwareUrl,
  } = useUpgradeContext();

  // Generic failure reporter: log with a label that says which operation failed,
  // and show the user a message about that same operation. Split out from
  // handleWriteError because backup and restore were routed through it too, and
  // told the user to "check the connection" for failures that never touched the
  // device — see handleSave/handleRestoreFileChosen below.
  const reportError = (logLabel, userMessage) => (error) => {
    console.error(`${logLabel}:`, error);
    toaster.push(
      <Message type="error" closable>
        {userMessage}
      </Message>,
      { placement: 'topCenter', duration: 5000 },
    );
  };

  // Shared failure path for every register write below: writeRegister/
  // writePasswordRegister/writeValueStringRegister all reject if the
  // underlying pubsub.publish rejects (e.g. device offline/IoT disconnect),
  // but every call site here fires the write without awaiting or catching
  // it, so a rejection previously vanished with no console trace and no
  // user-visible sign the write never reached the device.
  const handleWriteError = reportError(
    'Device register write failed',
    'Failed to write to device — check the connection and try again.',
  );

  // Backup writes the register values already read into memory out to a file.
  // It never contacts the device, so the message stays entirely about the file
  // and mentions the device not at all — saying "nothing was sent to the
  // device" would imply sending is normally part of a backup.
  const handleBackupError = reportError('Controller data backup failed', 'Failed to save the backup file.');

  // Restore can fail on the file (unreadable, invalid JSON, unexpected shape)
  // or on the register writes, and one catch covers both, so the message names
  // both causes. "Dropped" rather than "unreachable": the Restore button is
  // disabled until connectionState.ready && controllerResponded, so the device
  // was answering when the user clicked — it can only have gone away since.
  const handleRestoreError = reportError(
    'Controller data restore failed',
    'Restore failed — the file may not be a valid backup, or the connection may have dropped.',
  );

  const [deviceToEdit, setDeviceToEdit] = useState({});
  const [showQRCode, setShowQRCode] = useState(false);
  const [showEditDevice, setShowEditDevice] = useState(false);
  const [showAddInstaller, setShowAddInstaller] = useState(false);
  // Backup/Restore status line; updated after a backup download or a restore.
  // { text, tail } rather than a bare string: the filename is what matters and
  // it sits at the end, so it's pinned against truncation (see MiddleEllipsis).
  // These messages have no "/" to split on, so the tail has to be explicit.
  const [backupStatus, setBackupStatus] = useState(
    /** @type {{ text: string, tail?: string }} */
    ({ text: 'Backup or restore controller data' }),
  );
  const restoreInputRef = useRef(null);

  const [fixed, setFixed] = useState(false);
  // Default the Backup & Upgrade column to hidden; reveal it once the controller
  // has responded. The user can still toggle it either way at any time (its
  // controls stay disabled until the controller responds — see below).
  const [showCol4, setShowCol4] = useState(false);
  useEffect(() => {
    if (controllerResponded) setShowCol4(true);
  }, [controllerResponded]);

  if (!window.ResizeObserver) {
    window.ResizeObserver = ResizeObserver;
  }

  const handleEditDeviceShow = (device) => {
    setDeviceToEdit(device);
    setShowEditDevice(true);
  };

  const handleQRCodeShow = (device) => {
    setDeviceToEdit(device);
    setShowQRCode(true);
  };

  const handleEditOff = () => {
    props.onSetEditOff(!props.editOff);
  };

  const handleShowAddInstaller = () => {
    setShowAddInstaller(true);
  };

  /** @param {{ id: string, name: string, phone: string, address: string } | null} _result */
  const handleAddInstaller = (_result) => {
    setShowAddInstaller(false);
    // TODO: wire up createInfoRecord(id, name, modeltype) here if/when the
    // installer-info-record backend flow is implemented (see AddInstaller.jsx —
    // handleClose is called with { id, name, phone, address } on confirm).
  };

  const handleSync = () => {
    setSyncing(true);
    doReadAll();
  };

  const handleSave = async () => {
    try {
      const fileName = await props.onSaveData();
      if (fileName) setBackupStatus({ text: `Data backed up to ${fileName}`, tail: fileName });
    } catch (error) {
      handleBackupError(error);
    }
  };

  // Restore: the button opens the file chooser; once a backup file is picked we
  // parse it, send the register writes, then report which file it came from.
  const handleRestoreButton = () => {
    if (restoreInputRef.current) restoreInputRef.current.click();
  };

  const handleRestoreFileChosen = async (e) => {
    const file = e.target.files && e.target.files[0];
    // Reset the input so choosing the same file again still fires onChange.
    e.target.value = '';
    if (!file) return;
    try {
      await props.onLoadData(file);
      setBackupStatus({ text: `Data restored from ${file.name}`, tail: file.name });
    } catch (error) {
      handleRestoreError(error);
    }
  };

  const handleUpgrade = (value, item, e) => {
    onSetUpgradeFile({
      selectedFile: true,
      selectedFileName: value,
    });
  };

  // The last gate before an OTA command goes out. Deliberately the same
  // definition of "firmware URL" the store and the add-field enforce — an
  // absolute http(s) URL ending in .bin — rather than a weaker non-empty
  // check, so a bad shipped default or a corrupted selection can't be flashed
  // either. http is allowed for LAN test hosts; see validateFirmwareUrl.
  const hasFirmwareUrl = (name) => validateFirmwareUrl(name).valid;

  // Flash exactly what was validated. hasFirmwareUrl trims before checking, so
  // a padded selection passes the guard — without this the controller would be
  // handed the whitespace too.
  const firmwareUrlToSend = (name) => normaliseFirmwareUrl(name);

  // A finished upgrade — complete or failed — is terminal, and the status
  // register latches its terminal code until the next upgrade starts. That
  // latching is what lets both the button state and the row visibility below be
  // derived from the register, with no extra state to keep in step.
  // On failure useDeviceSubscription also leaves the in-progress flag set, so
  // the rows stay up; previously they unmounted the instant the error arrived
  // and the user never saw it.
  const upgradeFinished = (address) => {
    const value = getRegisterData(address)?.value;
    return value === upgradeResultCodes_t.UPGRADE_COMPLETE || value === upgradeResultCodes_t.UPGRADE_ERROR;
  };
  // Only a live upgrade disables the button; a finished one — complete or failed —
  // lets the user act again.
  const stillUpgrading = (inProgress, address) => inProgress === true && !upgradeFinished(address);
  // Keep the status/error rows up for a finished upgrade too, so success is
  // visible instead of the rows silently vanishing. The register latches its
  // terminal code until the next upgrade starts, which is what makes this work
  // without extra state; the rows clear when the next upgrade begins or the
  // device is switched (register data resets).
  const showUpgradeRows = (inProgress, address) => inProgress === true || upgradeFinished(address);

  // Which slot's registers an SWC board card reads. While an upgrade is running
  // that is the slot it was fired on, not the radio's current value — the radio
  // stays enabled during a flash, and the in-progress flag is per board rather
  // than per slot, so following the radio pointed the card at the other slot's
  // stale latched result. Switching to a slot holding an old UPGRADE_COMPLETE
  // re-enabled the Upgrade button mid-flash and showed "Upgrade Complete" over
  // a live upgrade. When nothing is running the radio is the right thing to
  // follow, so the card previews the slot the user is about to flash.
  const watchedSlot = (inProgress, upgradeSlot, selectedSlot) => (inProgress === true ? upgradeSlot : selectedSlot);
  const powerWatchedSlot = watchedSlot(updateInProgressPower, powerUpgradeSlot, powerBoardSlot);
  const ioWatchedSlot = watchedSlot(updateInProgressIO, ioUpgradeSlot, ioBoardSlot);
  const powerStatusRegister =
    powerWatchedSlot === 'backup' ? REGS.FWUPDATE_POWER_BOARD_BACKUP : REGS.FWUPDATE_POWER_BOARD_MAIN;
  const powerErrorRegister =
    powerWatchedSlot === 'backup' ? REGS.FWUPDATE_POWER_BOARD_BACKUP_ERROR : REGS.FWUPDATE_POWER_BOARD_MAIN_ERROR;
  const ioStatusRegister = ioWatchedSlot === 'backup' ? REGS.FWUPDATE_IO_BOARD_BACKUP : REGS.FWUPDATE_IO_BOARD_MAIN;
  const ioErrorRegister =
    ioWatchedSlot === 'backup' ? REGS.FWUPDATE_IO_BOARD_BACKUP_ERROR : REGS.FWUPDATE_IO_BOARD_MAIN_ERROR;

  // Backstop, not a live path: every Upgrade button disables itself on the same
  // hasFirmwareUrl() check startUpgrade makes, so this cannot fire today and
  // has no test — React suppresses mouse events on a disabled button from its
  // own fiber props, so the handler cannot be reached from a rendered tree even
  // with the DOM attribute stripped. Unlike the isBoardUpgradeConfigured guard
  // this replaced — which could never fire under any code path — this one
  // catches the disabled expression drifting away from the write, on a path
  // that flashes hardware.
  const warnNoFirmwareSelected = (what) => {
    devLog('Upgrade blocked: no valid firmware URL selected', what);
    toaster.push(
      <Message type="warning" closable>
        {`Select a valid firmware URL (http(s)://…/firmware.bin) before upgrading the ${what}.`}
      </Message>,
      { placement: 'topCenter', duration: 5000 },
    );
  };

  // The single path from an Upgrade button to an OTA write. Every card funnels
  // through here so the hasFirmwareUrl() guard exists once rather than once per
  // handler: there is no way to dispatch a flash that skips it.
  //
  // PR review found why that matters — the AquaChem and SmartHeater handlers
  // had no check at all, leaving the invariant to their buttons' disabled
  // expressions, while the comment above claimed every handler enforced it.
  // Duplicating the guard into those two would have restored the invariant but
  // kept four places for it to fall out of again; collapsing the write instead
  // means a new card cannot dispatch unguarded without deliberately bypassing
  // this function.
  const startUpgrade = ({ what, fileName, address, onStarted }) => {
    if (!hasFirmwareUrl(fileName)) {
      warnNoFirmwareSelected(what);
      return;
    }
    handleValueStringRegisterUpdate(getRegisterData(address), 6880, firmwareUrlToSend(fileName));
    onStarted();
  };

  // Drives REGS.FWUPDATE, whose meaning depends on the model: PSW firmware, the
  // AquaLight LC image, or the SWC control board. Each keeps its own selection
  // so one family's binary can never be armed on another's hardware.
  const handleUpgradeButton = () =>
    startUpgrade({
      what: mainFirmware.what,
      fileName: mainFirmware.selectedFileName,
      address: REGS.FWUPDATE,
      onStarted: () => setUpdateInProgress(true),
    });

  const handleLCUpgrade = (value) => {
    onSetUpgradeLCFile({ selectedFile: true, selectedFileName: value });
  };

  const handleControlBoardUpgrade = (value) => {
    onSetUpgradeControlBoardFile({ selectedFile: true, selectedFileName: value });
  };

  const handleACUpgrade = (value, item, e) => {
    // console.log ( e, e.target.value);
    onSetUpgradeACFile({
      selectedFile: true,
      //   // //**** added line below ****//
      selectedFileName: value,
    });
  };

  const handleSHUpgrade = (value, item, e) => {
    devLog('SH Upgrade', value, item, e);
    onSetUpgradeSHFile({
      selectedFile: true,
      //   // //**** added line below ****//
      selectedFileName: value,
    });
  };

  const handleACUpgradeButton = () =>
    startUpgrade({
      what: 'AquaChem',
      fileName: upgradeACFile?.selectedFileName,
      address: REGS.FWUPDATE_EXPANSION_PLUS,
      onStarted: () => onSetACUpdateInProgress(true),
    });

  const handleSHUpgradeButton = (upgradeExp2) => {
    devLog('SH button pressed', upgradeExp2);
    const isExp2 = upgradeExp2 === true;
    startUpgrade({
      what: `SmartHeater ${isExp2 ? 'EXP2' : 'EXP1'}`,
      fileName: upgradeSHFile?.selectedFileName,
      address: isExp2 ? REGS.FWUPDATE_EXP4_SLAVE2 : REGS.FWUPDATE_EXP4_SLAVE1,
      onStarted: () => (isExp2 ? onSetSH2UpdateInProgress(true) : onSetSH1UpdateInProgress(true)),
    });
  };

  const handlePowerUpgrade = (value) => {
    onSetUpgradePowerFile({ selectedFile: true, selectedFileName: value });
  };

  const handleIOUpgrade = (value) => {
    onSetUpgradeIOFile({ selectedFile: true, selectedFileName: value });
  };

  const handleBoardUpgradeButton = (board) => {
    const isPower = board === 'power';
    const slot = isPower ? powerBoardSlot : ioBoardSlot;
    const address = isPower
      ? slot === 'backup'
        ? REGS.FWUPDATE_POWER_BOARD_BACKUP
        : REGS.FWUPDATE_POWER_BOARD_MAIN
      : slot === 'backup'
        ? REGS.FWUPDATE_IO_BOARD_BACKUP
        : REGS.FWUPDATE_IO_BOARD_MAIN;
    const fileName = isPower ? upgradePowerFile?.selectedFileName : upgradeIOFile?.selectedFileName;

    startUpgrade({
      what: `${isPower ? 'Power' : 'IO'} board`,
      fileName,
      address,
      // Snapshot the slot alongside the flag, and only once the write has
      // actually been dispatched. The flag is per board and the radio stays
      // live during a flash, so the card must watch the slot the command went
      // to, not whatever the radio reads later.
      onStarted: () => {
        if (isPower) {
          setPowerUpgradeSlot(slot);
          onSetPowerUpdateInProgress(true);
        } else {
          setIOUpgradeSlot(slot);
          onSetIOUpdateInProgress(true);
        }
      },
    });
  };

  const doHandleEditDeviceUpdate = (device, name, userDevName, modeltype) => {
    setShowEditDevice(false);
    handleEditDeviceUpdate(device, name, userDevName, modeltype, updateDeviceName, updateUserDeviceName);
  };

  const doAddDBFunction = (record_ID, text, user) => {
    return client.graphql({
      query: createDevice,
      variables: {
        input: {
          recordType: RECORD_TYPES.NOTES,
          recordID: record_ID,
          notes: text,
          userID: user,
        },
      },
    });
  };

  const addNote = async (record_ID, text) => {
    const user = loginId;
    const result = await doAddDBFunction(record_ID, text, user);
    devLog('NOTE ADDING to DB', record_ID, text, result);
    if ('data' in result && result.data?.createDevice) {
      setNotesData((prevNotesData) => [...prevNotesData, result.data.createDevice]);
    } else {
      devLog('addNote: unexpected response', result);
    }
  };

  async function deleteNote(record_ID) {
    try {
      const result = await client.graphql({
        query: deleteDevice,
        variables: {
          input: {
            recordType: RECORD_TYPES.NOTES,
            recordID: record_ID,
          },
        },
      });

      devLog('Deleting note from DB', record_ID, result);
    } catch (error) {
      console.error('Error deleting note:', error);
    }
  }

  const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

  useEffect(() => {
    const timer = setTimeout(() => devLog('Time to update current info record.'), 3000);
    return () => clearTimeout(timer);
  }, []);

  const handleQRDeviceUpdate = () => {
    setShowQRCode(false);
  };

  const isUpgradePossible = () => {
    var upgradePossible = true;
    if (!connectionState.ready) {
      upgradePossible = false;
    }
    if (!controllerResponded) {
      // Controller hasn't answered yet — don't let the user fire an upgrade.
      upgradePossible = false;
    }
    if (stillUpgrading(updateInProgress, REGS.FWUPDATE)) {
      // Genuinely mid-flash. A *failed* main upgrade leaves the flag set (so its
      // error stays visible) but must not lock out every other card.
      upgradePossible = false;
    }
    if (props.editOff) {
      upgradePossible = false;
    }
    return upgradePossible;
  };

  /**
   * @param {boolean} fixed
   * @param {{
   *   device?: Device,
   *   connectionState: { ready: boolean, state: string },
   *   selectedDevice?: string,
   *   syncing?: boolean,
   *   editOff?: boolean,
   *   activeKey?: string | number,
   *   onSetActiveKey?: (key: any) => void,
   * }} props
   * @param {boolean} showCol4
   */
  function header_display(fixed, props, showCol4) {
    if (!props.device) return null;

    return (
      <div>
        <div
          className="grid grid-cols-1 lg:grid-cols-3 gap-4"
          style={fixed ? fixedHeaderCardStyle : unfixedHeaderCardStyle}
        >
          <div className="col-span-1">
            <Flex justifyContent="start" alignItems="center" className="space-x-2">
              <Button icon={IconBack} color={COLOURS.default} size="xs" variant="secondary" onClick={handleBack}>
                Back
              </Button>

              <Icon
                icon={connectionState.ready ? IconConnected : IconDisconnected}
                color={connectionState.ready ? COLOURS.good : COLOURS.error}
                size="lg"
              />
              <Metric color={connectionState.ready ? COLOURS.good : COLOURS.error} className="truncate">
                {selectedDevice !== undefined
                  ? connectionState.ready
                    ? connectionState.state
                    : connectionState.state
                  : connectionState.state}
              </Metric>
            </Flex>
          </div>
          <div className="col-span-1">
            <Flex justifyContent="center" alignItems="center" className="space-x-2">
              {props.device.modelType !== undefined ? (
                <img
                  src={getControllerIconText_FROM_STRING(props.device.modelType).icon}
                  alt="Controller Icon"
                  width="40"
                  height="40"
                />
              ) : (
                <div></div>
              )}

              <Metric>{props.device.userName}</Metric>
              {GetDeviceBadgeFromStatus(props.device)}
            </Flex>
            <Flex justifyContent="center" alignItems="center" className="space-x-4">
              {props.device.uniqueDeviceId !== undefined && (
                <TextTremor>{'UID:' + props.device.uniqueDeviceId}</TextTremor>
              )}
              {props.device.usersDeviceName !== 'Undefined' && <TextTremor>{props.device.usersDeviceName}</TextTremor>}
            </Flex>
          </div>
          <div className="col-span-1">
            <Flex justifyContent="end" className="space-x-4">
              <IconButton
                appearance="primary"
                color="blue"
                size="sm"
                icon={<IconEdit />}
                onClick={() => handleEditDeviceShow(props.device)}
                aria-label={`Edit ${props.device.userName || props.device.uniqueDeviceId}`}
              />
              <IconButton
                appearance="primary"
                color="blue"
                size="sm"
                icon={<IconQRCode />}
                onClick={() => handleQRCodeShow(props.device)}
                aria-label={`Show QR code for ${props.device.userName || props.device.uniqueDeviceId}`}
              />
              <Button
                icon={IconSync}
                variant={syncing ? 'light' : 'primary'}
                color={COLOURS.default}
                size="xs"
                disabled={!connectionState.ready}
                onClick={handleSync}
              >
                Sync
              </Button>

              <Button
                icon={IconEdit}
                color={COLOURS.default}
                size="xs"
                variant={props.editOff ? 'secondary' : 'primary'}
                disabled={!connectionState.ready}
                onClick={handleEditOff}
              >
                Edit
              </Button>
            </Flex>
          </div>
        </div>

        <CustomNavbar
          appearance="inverse"
          activeKey={props.activeKey}
          onSelect={props.onSetActiveKey}
          device={props.device} // <-- this is needed!
          showCol4={showCol4}
          onToggleCol4={() => setShowCol4((prev) => !prev)}
        />
      </div>
    );
  }

  let time = new Date().getTime();
  const setActivityTime = (e) => {
    time = new Date().getTime();
  };
  document.body.addEventListener('mousemove', setActivityTime);
  document.body.addEventListener('keypress', setActivityTime);

  // const doRefresh = () => {
  //   if (new Date().getTime() - time >= 10*60000) {
  //     (props.onRefresh())
  //  //   window.location.reload(true);
  //   } else {
  //     setTimeout(doRefresh, 10000);
  //   }
  // }
  // setTimeout(doRefresh, 10000);

  const handleRegisterUpdate = (register, value, do_refresh) => {
    const registerValue = parseInt(value, 10);
    if (registerValue !== register.value) {
      devLog(register, '=>', registerValue);
      writeRegister(register.address, registerValue).catch(handleWriteError);
      if (do_refresh) {
        delay(2000).then(() => {
          doReadAll();
          devLog(' refreshing all registers now');
        });
      }
    }
  };

  const handleFilterTimerEnableChange = (register, value) => {
    // const registerValue = parseInt(value, 10);
    var currentFilterEnable = getRegisterData(REGS.FILTER_TIME_ENABLE);
    var newFilterEnable = 0;

    devLog('handleFilterTimerEnableChange register:', register, ' value ', value);

    switch (register.address) {
      case REGS.FILTER_TIME_1_START_TIME:
        if (value === false) {
          newFilterEnable = currentFilterEnable.value & ~1;
        } else {
          newFilterEnable = currentFilterEnable.value | 1;
        }
        devLog('CFE', currentFilterEnable.value, ': ', newFilterEnable);
        break;
      case REGS.FILTER_TIME_2_START_TIME:
        if (value === false) {
          newFilterEnable = currentFilterEnable.value & ~2;
        } else {
          newFilterEnable = currentFilterEnable.value | 2;
        }
        break;
      case REGS.FILTER_TIME_3_START_TIME:
        if (value === false) {
          newFilterEnable = currentFilterEnable.value & ~4;
        } else {
          newFilterEnable = currentFilterEnable.value | 4;
        }
        break;
      case REGS.FILTER_TIME_4_START_TIME:
        if (value === false) {
          newFilterEnable = currentFilterEnable.value & ~8;
        } else {
          newFilterEnable = currentFilterEnable.value | 8;
        }
        break;
      default:
        break;
    }

    if (newFilterEnable !== currentFilterEnable.value) {
      devLog(REGS.FILTER_TIME_ENABLE, '=>', newFilterEnable);
      writeRegister(REGS.FILTER_TIME_ENABLE, newFilterEnable).catch(handleWriteError);
    }
  };

  const handlePrivateRegisterUpdate = (register, value) => {
    const registerValue = parseInt(value, 10);
    if (registerValue !== register.value) {
      devLog('registerChanged Secure', register, '=>', registerValue);
      writePasswordRegister(register.address, registerValue).catch(handleWriteError);
    }
  };

  const handleValueStringRegisterUpdate = (register, value, valueString) => {
    const registerValue = parseInt(value, 10);
    devLog('registerChanged With ValueString', register, '=>', registerValue);
    writeValueStringRegister(register.address, registerValue, valueString).catch(handleWriteError);
  };

  const handleTimesUpdate = (register, value) => {
    let hours = getHours(value);
    let minutes = getMinutes(value);
    let timeValue = (hours << 8) | minutes;
    devLog('Date_range_times', value, timeValue, hours, minutes);

    if (timeValue !== register.value) {
      devLog('times registerupdate', register, '=>', timeValue);
      writeRegister(register.address, timeValue).catch(handleWriteError);
    }
  };

  const handleNameRegisterUpdate = (register, value) => {
    devLog('handle name =>', value);

    var name_idx = 0;
    var registerValues = [];

    for (let itr = 0; itr < 8; itr++) {
      registerValues.push((value.charCodeAt(name_idx) << 8) | value.charCodeAt(name_idx + 1));
      name_idx = name_idx + 2;
    }

    devLog(register.address, '=>', registerValues);
    writeRegisters(register.address, registerValues).catch(handleWriteError);
    doReadAll();
  };

  const getRegisterData = (address) => registerData.find((item) => item.address === address);

  const handleBack = () => {
    props.onBackPressed();
  };

  // No production SmartHeater OTA URLs yet, and no way to add one: the picker
  // below is not creatable and this card predates FirmwareUpgradeCard, so it
  // has no validated "Add firmware URL" field either. That leaves SmartHeater
  // inert, which matches APP_CONFIG.SMART_HEATER being off. Before enabling it,
  // give it an initSmartHeaterUrls list and move the card onto
  // FirmwareUpgradeCard so it picks up validation, add and remove.
  const PSWEXP4Upgradedata = [];

  useLogChange('upgrade in progress', updateInProgress);
  useLogChange('AquaChem upgrade in progress', updateInProgressAC);
  useLogChange('SmartHeater upgrade in progress', [updateInProgressSH1, updateInProgressSH2]);

  // navProps, not props: these are CustomNavbar's own props, passed down by
  // header_display. showCol4/onToggleCol4 in particular are DeviceView's local
  // state and its setter — naming the rest object `props` made them read like
  // DeviceView's props and the toggle look unwired.
  const CustomNavbar = ({ onSelect, activeKey, ...navProps }) => {
    return (
      <Navbar {...navProps}>
        <Nav onSelect={onSelect} activeKey={activeKey}>
          <Nav.Item eventKey={tabItems.home} icon={<IconHome />}>
            <div className="hidden md:block">Home</div>
          </Nav.Item>
          {navProps.device && !is_a_light_controller(navProps.device.modelType) && (
            <Nav.Item eventKey={tabItems.trends} icon={<IconTrends />}>
              <div className="hidden md:block">Trends</div>
            </Nav.Item>
          )}
          <Nav.Item eventKey={tabItems.appliances} icon={<IconTrends />}>
            <div className="hidden md:block">Appliances</div>
          </Nav.Item>
          <Nav.Item eventKey={tabItems.faults} icon={<IconError />}>
            <div className="hidden md:block">Faults</div>
          </Nav.Item>
          <Nav.Item eventKey={tabItems.control} icon={<IconControl />}>
            <div className="hidden md:block">Control</div>
          </Nav.Item>
          {navProps.device && !is_a_light_controller(navProps.device.modelType) && (
            <>
              <Nav.Item eventKey={tabItems.heating} icon={<IconHeat />}>
                <div className="hidden md:block">Heating</div>
              </Nav.Item>
              <Nav.Item eventKey={tabItems.solar} icon={<IconSolar />}>
                <div className="hidden md:block">Solar</div>
              </Nav.Item>
              <Nav.Item eventKey={tabItems.chemical} icon={<IconAcidTube />}>
                <div className="hidden md:block">Chemical</div>
              </Nav.Item>
              <Nav.Item eventKey={tabItems.spa} icon={<IconSpa />}>
                <div className="hidden md:block">SPA</div>
              </Nav.Item>
              <Nav.Item eventKey={tabItems.pump} icon={<IconPump />}>
                <div className="hidden md:block">Pump</div>
              </Nav.Item>{' '}
            </>
          )}
          <Nav.Item eventKey={tabItems.applianceSetup} icon={<IconSettings />}>
            <div className="hidden md:block">Appliance Setup</div>
          </Nav.Item>
          <Nav.Item eventKey={tabItems.system} icon={<IconSettings />}>
            <div className="hidden md:block">System</div>
          </Nav.Item>
          {navProps.device && !is_a_light_controller(navProps.device.modelType) && (
            <Nav.Item eventKey={tabItems.filter} icon={<IconFrozen />}>
              <div className="hidden md:block">Filter</div>
            </Nav.Item>
          )}
        </Nav>
        {/* Firmware/Backup column toggle: Home is the only tab that renders
            the column, so the toggle would do nothing anywhere else. It stays
            available regardless of whether the controller has responded — the
            column auto-reveals on first response, but the user can still open
            it before then to see the panel with its controls disabled. */}
        {activeKey === tabItems.home && (
          <Nav pullRight>
            <Nav.Item
              onClick={navProps.onToggleCol4}
              icon={navProps.showCol4 ? <IconMinus /> : <IconAdd />}
              aria-label={navProps.showCol4 ? 'Hide firmware & backup panel' : 'Show firmware & backup panel'}
            >
              Backup & Upgrade
            </Nav.Item>
          </Nav>
        )}
      </Navbar>
    );
  };

  devLog(
    'device:',
    props.device,
    'modelType:',
    props.device && props.device.modelType,
    'is_a_light_controller:',
    props.device && is_a_light_controller(props.device.modelType),
  );

  // Firmware-card gating + URL-list selection (beta vs release).
  const pswUrls = APP_CONFIG.BETA_VERSIONS === true ? PSWUpgradeURLS_beta : PSWUpgradeURLS;
  const aqualightUrls = APP_CONFIG.BETA_VERSIONS === true ? AquaLightUpgradeURLS_beta : AquaLightUpgradeURLS;
  const deviceIsLightController = props.device ? is_a_light_controller(props.device.modelType) : false;
  const deviceIsSWC = props.device ? is_a_SWC(props.device) : false;
  // AquaChem is present only when SLAVE_3_ENABLE reads 1.
  const aquachemDetected = getRegisterData(REGS.SLAVE_3_ENABLE)?.value === 1;

  // The card that drives REGS.FWUPDATE. The register is the same in all three
  // cases but the image behind it is not, so each family carries its own URL
  // list, its own selection and its own persistence key. (No beta split for the
  // SWC boards: only one image stream is published per board today.)
  // Note these are derived on every render while the cards only mount behind
  // showCol4, so the upgrade props may legitimately be absent — hence `?.`.
  const mainFirmware = deviceIsSWC
    ? {
        title: 'Upgrade Control Board',
        what: 'control board',
        urls: ControlBoardUpgradeURLS,
        urlKey: FIRMWARE_URL_KEYS.controlboard,
        selectedFileName: upgradeControlBoardFile?.selectedFileName,
        onSelect: handleControlBoardUpgrade,
        onAddUrl: onaddControlBoardUrl,
      }
    : deviceIsLightController
      ? {
          title: 'Upgrade LC Firmware',
          what: 'light controller',
          urls: aqualightUrls,
          urlKey: APP_CONFIG.BETA_VERSIONS === true ? FIRMWARE_URL_KEYS.aqualight_beta : FIRMWARE_URL_KEYS.aqualight,
          selectedFileName: upgradeLCFile?.selectedFileName,
          onSelect: handleLCUpgrade,
          onAddUrl: onaddAqualightUrl,
        }
      : {
          title: 'Upgrade Firmware',
          what: 'controller',
          urls: pswUrls,
          urlKey: APP_CONFIG.BETA_VERSIONS === true ? FIRMWARE_URL_KEYS.psw_beta : FIRMWARE_URL_KEYS.psw,
          selectedFileName: upgradeFile?.selectedFileName,
          onSelect: handleUpgrade,
          onAddUrl: onaddPSWUrl,
        };

  return (
    <div className="mt-3">
      <div className="grid grid-cols-1 lg:grid-cols-1 gap-4">
        <div className="col-span-1">
          <Affix onChange={(affixed) => setFixed(affixed ?? false)}>{header_display(fixed, props, showCol4)}</Affix>

          {props.activeKey === tabItems.home && (
            <ViewErrorBoundary name="Home">
              <Panel bordered bodyFill>
                <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
                  <div className={showCol4 ? 'col-span-1 lg:col-span-3' : 'col-span-1 lg:col-span-4'}>
                    <SystemInfo
                      onGetRegisterData={getRegisterData}
                      handleRegisterUpdate={handleRegisterUpdate}
                      onNameRegisterUpdate={handleNameRegisterUpdate}
                      onTimesUpdate={handleTimesUpdate}
                      formatTemperature={props.formatTemperature}
                      onPrivateRegisterUpdate={handlePrivateRegisterUpdate}
                      registerData={registerData}
                      editOff={props.editOff}
                      device={props.device}
                    />
                  </div>
                  {showCol4 && (
                    <div className="hidden md:block">
                      <Card decoration="bottom" decorationColor={COLOURS.cardDecoration} className="mt-3 space-y-2">
                        <Flex justifyContent="center" className="space-x-4 ">
                          <Title>
                            <Bold>Backup/Restore</Bold>
                          </Title>
                        </Flex>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                          <Flex justifyContent="start" className="space-x-4 ">
                            {/* minWidth/width let the flex child actually shrink, which
                              is what gives MiddleEllipsis something to truncate. */}
                            <TextTremor
                              data-testid="backup-status"
                              style={{ minWidth: 0, width: '100%', overflow: 'hidden' }}
                            >
                              <MiddleEllipsis text={backupStatus.text} tail={backupStatus.tail} />
                            </TextTremor>
                          </Flex>
                          <input
                            type="file"
                            accept=".json,application/json,text/plain"
                            ref={restoreInputRef}
                            style={{ display: 'none' }}
                            onChange={handleRestoreFileChosen}
                          />
                          <Flex justifyContent="end" className="space-x-4 ">
                            <Button
                              icon={IconSync}
                              variant={props.saving ? 'light' : 'primary'}
                              color={COLOURS.default}
                              size="xs"
                              disabled={!connectionState.ready || !controllerResponded}
                              onClick={handleSave}
                            >
                              Backup
                            </Button>
                            <Button
                              icon={IconSync}
                              variant={props.saving ? 'light' : 'primary'}
                              color={COLOURS.default}
                              size="xs"
                              disabled={!connectionState.ready || !controllerResponded || props.editOff}
                              onClick={handleRestoreButton}
                            >
                              Restore
                            </Button>
                          </Flex>
                        </div>
                      </Card>
                      {props.device && (
                        <>
                          {/* One card drives REGS.FWUPDATE for every model; mainFirmware
                            resolves which family's list, selection and key it uses. */}
                          <FirmwareUpgradeCard
                            title={mainFirmware.title}
                            urls={mainFirmware.urls}
                            urlKey={mainFirmware.urlKey}
                            selectedFileName={mainFirmware.selectedFileName}
                            onSelect={mainFirmware.onSelect}
                            onAddUrl={mainFirmware.onAddUrl}
                            onRemoveUrl={onRemoveFirmwareUrl}
                            showProgress={showUpgradeRows(updateInProgress, REGS.FWUPDATE)}
                            progressRegisters={[getRegisterData(REGS.FWUPDATE), getRegisterData(REGS.FWUPDATE_ERROR)]}
                          >
                            <Button
                              icon={IconSync}
                              variant={props.saving ? 'light' : 'primary'}
                              color={COLOURS.default}
                              size="xs"
                              disabled={!isUpgradePossible() || !hasFirmwareUrl(mainFirmware.selectedFileName)}
                              onClick={handleUpgradeButton}
                            >
                              Upgrade
                            </Button>
                          </FirmwareUpgradeCard>
                          {!deviceIsLightController && (
                            <>
                              {/* AquaChem: PSW models only (not SWC), and only when detected (SLAVE_3_ENABLE === 1) */}
                              {!deviceIsSWC && aquachemDetected && (
                                <FirmwareUpgradeCard
                                  title="Upgrade AquaChem Firmware"
                                  urls={AquaChemUpgradeURLS}
                                  urlKey={FIRMWARE_URL_KEYS.aquachem}
                                  selectedFileName={upgradeACFile?.selectedFileName}
                                  onSelect={handleACUpgrade}
                                  onAddUrl={onaddACUrl}
                                  onRemoveUrl={onRemoveFirmwareUrl}
                                  showProgress={showUpgradeRows(updateInProgressAC, REGS.FWUPDATE_EXPANSION_PLUS)}
                                  progressRegisters={[getRegisterData(REGS.FWUPDATE_EXPANSION_PLUS)]}
                                >
                                  <Button
                                    icon={IconSync}
                                    variant={props.saving ? 'light' : 'primary'}
                                    color={COLOURS.default}
                                    size="xs"
                                    disabled={
                                      !isUpgradePossible() ||
                                      stillUpgrading(updateInProgressAC, REGS.FWUPDATE_EXPANSION_PLUS) ||
                                      !hasFirmwareUrl(upgradeACFile?.selectedFileName)
                                    }
                                    onClick={handleACUpgradeButton}
                                  >
                                    Upgrade
                                  </Button>
                                </FirmwareUpgradeCard>
                              )}
                              {APP_CONFIG.SMART_HEATER && (
                                <Card
                                  decoration="bottom"
                                  decorationColor={COLOURS.cardDecoration}
                                  className="mt-3 space-y-2"
                                >
                                  <Flex justifyContent="center" className="space-x-4 ">
                                    <Title>
                                      <Bold>Upgrade SmartHeater Firmware</Bold>
                                    </Title>
                                  </Flex>
                                  <Flex justifyContent="between" className="space-x-4 ">
                                    {/* Not `creatable`, for the same reason as
                                  FirmwareUpgradeCard: onCreate only logged, yet
                                  rsuite still selected whatever was typed, so
                                  an arbitrary string could end up armed here.
                                  This card has no URL list yet (see
                                  PSWEXP4Upgradedata) and SMART_HEATER is off —
                                  it needs its own initSmartHeaterUrls and a
                                  move onto FirmwareUpgradeCard before use. */}
                                    <InputPicker
                                      defaultValue={PSWEXP4Upgradedata[0]?.value}
                                      data={PSWEXP4Upgradedata}
                                      style={{ width: '100%' }}
                                      value={upgradeSHFile?.selectedFileName}
                                      onSelect={handleSHUpgrade}
                                    />
                                  </Flex>
                                  {showUpgradeRows(updateInProgressSH1, REGS.FWUPDATE_EXP4_SLAVE1) && (
                                    <div>
                                      <DisplayItem
                                        icon={IconInfo}
                                        register={getRegisterData(REGS.FWUPDATE_EXP4_SLAVE1)}
                                        borderOff={true}
                                      />
                                    </div>
                                  )}
                                  {showUpgradeRows(updateInProgressSH2, REGS.FWUPDATE_EXP4_SLAVE2) && (
                                    <div>
                                      <DisplayItem
                                        icon={IconInfo}
                                        register={getRegisterData(REGS.FWUPDATE_EXP4_SLAVE2)}
                                        borderOff={true}
                                      />
                                    </div>
                                  )}
                                  <Flex justifyContent="center" className="space-x-4 ">
                                    <Button
                                      icon={IconSync}
                                      variant={props.saving ? 'light' : 'primary'}
                                      color={COLOURS.default}
                                      size="xs"
                                      disabled={
                                        !isUpgradePossible() ||
                                        stillUpgrading(updateInProgressSH1, REGS.FWUPDATE_EXP4_SLAVE1) ||
                                        !hasFirmwareUrl(upgradeSHFile?.selectedFileName)
                                      }
                                      onClick={() => {
                                        handleSHUpgradeButton(false);
                                      }}
                                    >
                                      Upgrade EXP1
                                    </Button>
                                    <Button
                                      icon={IconSync}
                                      variant={props.saving ? 'light' : 'primary'}
                                      color={COLOURS.default}
                                      size="xs"
                                      disabled={
                                        !isUpgradePossible() ||
                                        stillUpgrading(updateInProgressSH2, REGS.FWUPDATE_EXP4_SLAVE2) ||
                                        !hasFirmwareUrl(upgradeSHFile?.selectedFileName)
                                      }
                                      onClick={() => {
                                        handleSHUpgradeButton(true);
                                      }}
                                    >
                                      Upgrade EXP2
                                    </Button>
                                  </Flex>
                                </Card>
                              )}
                              {/* SWC-only: Power board & IO board, each with a main/backup slot toggle */}
                              {deviceIsSWC && (
                                <FirmwareUpgradeCard
                                  title="Upgrade Power Board"
                                  urls={PowerBoardUpgradeURLS}
                                  urlKey={FIRMWARE_URL_KEYS.powerboard}
                                  selectedFileName={upgradePowerFile?.selectedFileName}
                                  onSelect={handlePowerUpgrade}
                                  onAddUrl={onaddPowerUrl}
                                  onRemoveUrl={onRemoveFirmwareUrl}
                                  showProgress={showUpgradeRows(updateInProgressPower, powerStatusRegister)}
                                  progressRegisters={[
                                    getRegisterData(powerStatusRegister),
                                    getRegisterData(powerErrorRegister),
                                  ]}
                                >
                                  <RadioGroup
                                    inline
                                    name="powerSlot"
                                    value={powerBoardSlot}
                                    onChange={setPowerBoardSlot}
                                  >
                                    <Radio value="main">Main</Radio>
                                    <Radio value="backup">Backup</Radio>
                                  </RadioGroup>
                                  <Button
                                    icon={IconSync}
                                    variant={props.saving ? 'light' : 'primary'}
                                    color={COLOURS.default}
                                    size="xs"
                                    disabled={
                                      !isUpgradePossible() ||
                                      stillUpgrading(updateInProgressPower, powerStatusRegister) ||
                                      !hasFirmwareUrl(upgradePowerFile?.selectedFileName)
                                    }
                                    onClick={() => handleBoardUpgradeButton('power')}
                                  >
                                    Upgrade
                                  </Button>
                                </FirmwareUpgradeCard>
                              )}
                              {deviceIsSWC && (
                                <FirmwareUpgradeCard
                                  title="Upgrade IO Board"
                                  urls={IOBoardUpgradeURLS}
                                  urlKey={FIRMWARE_URL_KEYS.ioboard}
                                  selectedFileName={upgradeIOFile?.selectedFileName}
                                  onSelect={handleIOUpgrade}
                                  onAddUrl={onaddIOUrl}
                                  onRemoveUrl={onRemoveFirmwareUrl}
                                  showProgress={showUpgradeRows(updateInProgressIO, ioStatusRegister)}
                                  progressRegisters={[
                                    getRegisterData(ioStatusRegister),
                                    getRegisterData(ioErrorRegister),
                                  ]}
                                >
                                  <RadioGroup inline name="ioSlot" value={ioBoardSlot} onChange={setIOBoardSlot}>
                                    <Radio value="main">Main</Radio>
                                    <Radio value="backup">Backup</Radio>
                                  </RadioGroup>
                                  <Button
                                    icon={IconSync}
                                    variant={props.saving ? 'light' : 'primary'}
                                    color={COLOURS.default}
                                    size="xs"
                                    disabled={
                                      !isUpgradePossible() ||
                                      stillUpgrading(updateInProgressIO, ioStatusRegister) ||
                                      !hasFirmwareUrl(upgradeIOFile?.selectedFileName)
                                    }
                                    onClick={() => handleBoardUpgradeButton('io')}
                                  >
                                    Upgrade
                                  </Button>
                                </FirmwareUpgradeCard>
                              )}
                            </>
                          )}
                        </>
                      )}
                    </div>
                  )}
                  {/* {!showCol4 && (
                  <div className="hidden md:block transition-all duration-500">
                    <Button
                      color={COLOURS.default}
                      size="xs"
                      className="mb-2"
                      onClick={() => setShowCol4(true)}
                    >
                      Show Details
                    </Button>
                  </div>
                )} */}
                </div>
                <Card>
                  <Notes device={props.device} onAddNote={addNote} onDeleteNote={deleteNote} />
                </Card>
              </Panel>
            </ViewErrorBoundary>
          )}
          {props.activeKey === tabItems.trends && (
            <ViewErrorBoundary name="Trends">
              <Card>
                <TrendsView
                // pool={props.pool}
                // water={props.water}
                // roof={props.roof}
                // poolLabels={props.poolLabels}
                // applianceDataArray={props.applianceDataArray}
                // onViewFaults={handleViewFaults}
                // onViewDashboard={handleViewDashboard}
                />
              </Card>
            </ViewErrorBoundary>
          )}
          {props.activeKey === tabItems.appliances && (
            <ViewErrorBoundary name="Appliances">
              <Card>
                <AppliancesView params={props.params} />
              </Card>
            </ViewErrorBoundary>
          )}
          {props.activeKey === tabItems.faults && (
            <ViewErrorBoundary name="Faults">
              <Card>
                <HistoryView
                  // selectedDevices={props.selectedDevices}
                  device={props.device}
                  onBackPressed={handleBack}
                  // latestFaults={props.latestFaults}

                  onSetSelectedFaultView={props.onSetSelectedFaultView}
                  // onSetSelectedLatestFaults={props.onSetSelectedLatestFaults}
                  selectedFaultView={props.selectedFaultView}
                  // selectedLatestFaults ={props.selectedLatestFaults}
                  selectedHistoryFaults={props.selectedHistoryFaults}
                  onSetSelectedHistoryFaults={props.onSetSelectedHistoryFaults}
                />
              </Card>
            </ViewErrorBoundary>
          )}
          {props.activeKey === tabItems.control && (
            <ViewErrorBoundary name="Control">
              <ControlView
                editOff={props.editOff}
                onGetRegisterData={getRegisterData}
                handleRegisterUpdate={handleRegisterUpdate}
                onTimesUpdate={handleTimesUpdate}
                formatTemperature={props.formatTemperature}
                device={props.device}
              />
            </ViewErrorBoundary>
          )}
          {props.activeKey === tabItems.heating && (
            <ViewErrorBoundary name="Heating">
              <HeatingView
                onGetRegisterData={getRegisterData}
                handleRegisterUpdate={handleRegisterUpdate}
                onTimesUpdate={handleTimesUpdate}
                formatTemperature={props.formatTemperature}
                formatTrackingTemperature={props.formatTrackingTemperature}
                editOff={props.editOff}
              />
            </ViewErrorBoundary>
          )}
          {props.activeKey === tabItems.solar && (
            <ViewErrorBoundary name="Solar">
              <SolarView
                onGetRegisterData={getRegisterData}
                handleRegisterUpdate={handleRegisterUpdate}
                onTimesUpdate={handleTimesUpdate}
                formatTemperature={props.formatTemperature}
                editOff={props.editOff}
              />
            </ViewErrorBoundary>
          )}
          {props.activeKey === tabItems.chemical && (
            <ViewErrorBoundary name="Chemical">
              <ChemicalView
                onGetRegisterData={getRegisterData}
                handleRegisterUpdate={handleRegisterUpdate}
                editOff={props.editOff}
              />
            </ViewErrorBoundary>
          )}
          {props.activeKey === tabItems.spa && (
            <ViewErrorBoundary name="SPA">
              <SPAView
                onGetRegisterData={getRegisterData}
                handleRegisterUpdate={handleRegisterUpdate}
                onTimesUpdate={handleTimesUpdate}
                editOff={props.editOff}
              />
            </ViewErrorBoundary>
          )}
          {props.activeKey === tabItems.pump && (
            <ViewErrorBoundary name="Pump">
              <PumpView
                onGetRegisterData={getRegisterData}
                handleRegisterUpdate={handleRegisterUpdate}
                onTimesUpdate={handleTimesUpdate}
                formatTemperature={props.formatTemperature}
                editOff={props.editOff}
              />
            </ViewErrorBoundary>
          )}
          {props.activeKey === tabItems.applianceSetup && (
            <ViewErrorBoundary name="Appliance Setup">
              <Panel bordered bodyFill>
                <ApplianceSetupView
                  onGetRegisterData={getRegisterData}
                  onNameRegisterUpdate={handleNameRegisterUpdate}
                  handleRegisterUpdate={handleRegisterUpdate}
                  onTimesUpdate={handleTimesUpdate}
                  editOff={props.editOff}
                  formatTemperature={props.formatTemperature}
                  device={props.device}
                />
              </Panel>
            </ViewErrorBoundary>
          )}
          {props.activeKey === tabItems.system && (
            <ViewErrorBoundary name="System">
              <Panel bordered bodyFill>
                <SystemSettingsView
                  onGetRegisterData={getRegisterData}
                  onNameRegisterUpdate={handleNameRegisterUpdate}
                  handleRegisterUpdate={handleRegisterUpdate}
                  onPrivateRegisterUpdate={handlePrivateRegisterUpdate}
                  onTimesUpdate={handleTimesUpdate}
                  editOff={props.editOff}
                  formatTemperature={props.formatTemperature}
                  device={props.device}
                />
              </Panel>
            </ViewErrorBoundary>
          )}
          {props.activeKey === tabItems.filter && (
            <ViewErrorBoundary name="Filter">
              <FilterView
                onGetRegisterData={getRegisterData}
                handleRegisterUpdate={handleRegisterUpdate}
                handleFilterTimerEnableChange={handleFilterTimerEnableChange}
                onTimesUpdate={handleTimesUpdate}
                editOff={props.editOff}
                formatTemperature={props.formatTemperature}
              />
            </ViewErrorBoundary>
          )}
        </div>
        <ShowDeviceQR open={showQRCode} device={deviceToEdit} handleClose={handleQRDeviceUpdate} />
        <EditDevice open={showEditDevice} device={deviceToEdit} handleClose={doHandleEditDeviceUpdate} />
        <AddInstaller open={showAddInstaller} handleClose={handleAddInstaller} />
      </div>
    </div>
  );
};
